import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const BusinessHeader = ({ business, onScanQR, onWhatsApp }) => {
  const [showQRModal, setShowQRModal] = useState(false);

  const handleScanClick = () => {
    setShowQRModal(true);
    if (onScanQR) onScanQR();
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(`Hola ${business?.name}, vi tu perfil en BIZZNET y me interesa conocer más sobre tus servicios.`);
    window.open(`https://wa.me/${business?.whatsapp}?text=${message}`, '_blank');
    if (onWhatsApp) onWhatsApp();
  };

  const handleSocialClick = (platform, url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <>
      <div className="bg-card rounded-lg shadow-card p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Business Logo */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-lg overflow-hidden bg-muted">
              <Image
                src={business?.logo}
                alt={business?.logoAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Business Information */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">{business?.name}</h1>
                <div className="flex items-center gap-2 text-muted-foreground mb-3">
                  <Icon name="Tag" size={18} />
                  <span className="text-sm">{business?.category}</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent rounded-lg">
                  <Icon name="Percent" size={20} color="var(--color-accent-foreground)" />
                  <span className="text-lg font-bold text-accent-foreground">
                    {business?.discountPercentage}% de descuento
                  </span>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2 bg-muted px-4 py-2 rounded-lg">
                <Icon name="Star" size={20} color="#F59E0B" />
                <span className="text-lg font-semibold text-foreground">{business?.rating}</span>
                <span className="text-sm text-muted-foreground">({business?.reviewCount} reseñas)</span>
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="MapPin" size={16} color="var(--color-primary)" />
                <span>{business?.address}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Phone" size={16} color="var(--color-primary)" />
                <a href={`tel:${business?.phone}`} className="hover:text-primary transition-colors">
                  {business?.phone}
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Mail" size={16} color="var(--color-primary)" />
                <a href={`mailto:${business?.email}`} className="hover:text-primary transition-colors">
                  {business?.email}
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Clock" size={16} color="var(--color-primary)" />
                <span>{business?.hours}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                variant="default"
                size="lg"
                iconName="QrCode"
                iconPosition="left"
                onClick={handleScanClick}
                className="flex-1 sm:flex-none"
              >
                Escanear QR
              </Button>
              <Button
                variant="success"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                onClick={handleWhatsAppClick}
                className="flex-1 sm:flex-none"
              >
                WhatsApp
              </Button>
              {business?.socialMedia?.facebook && (
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Facebook"
                  onClick={() => handleSocialClick('facebook', business?.socialMedia?.facebook)}
                />
              )}
              {business?.socialMedia?.instagram && (
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Instagram"
                  onClick={() => handleSocialClick('instagram', business?.socialMedia?.instagram)}
                />
              )}
              {business?.socialMedia?.website && (
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Globe"
                  onClick={() => handleSocialClick('website', business?.socialMedia?.website)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black/50 z-[2000] flex items-center justify-center p-4" onClick={() => setShowQRModal(false)}>
          <div className="bg-card rounded-lg shadow-modal max-w-md w-full p-6" onClick={(e) => e?.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-foreground">Código QR</h3>
              <button onClick={() => setShowQRModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={24} />
              </button>
            </div>
            <div className="bg-white p-6 rounded-lg mb-4 flex items-center justify-center">
              <Image
                src={business?.qrCode}
                alt={`Código QR para ${business?.name} - escanea para obtener ${business?.discountPercentage}% de descuento en productos y servicios`}
                className="w-64 h-64 object-contain"
              />
            </div>
            <div className="bg-accent/10 border border-accent rounded-lg p-4 mb-4">
              <p className="text-sm text-foreground mb-2">
                <strong>Instrucciones:</strong>
              </p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Muestra este código QR al personal del negocio</li>
                <li>Ellos escanearán el código para registrar tu visita</li>
                <li>Recibirás tu descuento de {business?.discountPercentage}% en tu compra</li>
              </ol>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Válido solo para miembros activos de BIZZNET
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default BusinessHeader;